import {
	AfterViewInit,
	Component,
	DestroyRef,
	ElementRef,
	HostListener,
	inject,
	OnInit,
	ChangeDetectionStrategy,
	forwardRef,
	input,
	viewChild,
} from '@angular/core';
import { NavigationService } from '../../services/navigation.service';
import { LayoutService } from '../../services/layout.service';
import { ConfigService } from '../../config/config.service';
import { map, startWith, switchMap } from 'rxjs/operators';
import { PopoverService } from '../../components/popover/popover.service';
import { PopoverRef } from '../../components/popover/popover-ref';
import { Observable, of, Subscription } from 'rxjs';
import { UserMenuComponent } from '../../components/user-menu/user-menu.component';
import { MatDialog } from '@angular/material/dialog';
import { SearchModalComponent } from '../../components/search-modal/search-modal.component';
import { DevService } from '../../../app/services/dev.service';
import { SessionService } from '../../../app/services/session.service';
import { KeycloakService } from '../../../app/auth/keycloak.service';
import { AraRealestateSearchDialogComponent } from '../../../app/components/ara-realestate-search-dialog/ara-realestate-search-dialog.component';
import { fadeInUp400ms } from '../../animations/fade-in-up.animation';
import { Config } from '../../../app/config';
import { trackByRoute } from '../../utils/track-by';
import { RecentRealEstateCacheService, RecentRealEstate } from '../../services/recent-real-estate-cache.service';
import { RecentPanelComponent, RecentPanelData } from './recent-panel/recent-panel.component';
import { Router } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { ScrollbarComponent } from '../../components/scrollbar/scrollbar.component';
import { SidenavItemComponent } from './sidenav-item/sidenav-item.component';

import { AsyncPipe } from '@angular/common';

@Component({
	selector: 'vex-sidenav',
	templateUrl: './sidenav.component.html',
	styleUrls: ['./sidenav.component.scss'],
	animations: [fadeInUp400ms],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [MatIcon, MatTooltip, ScrollbarComponent, SidenavItemComponent, forwardRef(() => SidenavComponent), AsyncPipe],
})
export class SidenavComponent implements OnInit, AfterViewInit {
	private navigationService = inject(NavigationService);
	private layoutService = inject(LayoutService);
	private configService = inject(ConfigService);
	private readonly popoverService = inject(PopoverService);
	private readonly dialog = inject(MatDialog);
	private impersonationService = inject(DevService);
	private sessionService = inject(SessionService);

	private readonly destroyRef = inject(DestroyRef);
	private readonly router = inject(Router);
	protected readonly pinService = inject(RecentRealEstateCacheService);
	private readonly keycloak = inject(KeycloakService);

	readonly collapsed = input<boolean>(undefined);
	collapsedOpen$ = this.layoutService.sidenavCollapsedOpen$;
	title$ = this.configService.config$.pipe(map((config) => config.sidenav.title));
	imageUrl$ = this.configService.config$.pipe(map((config) => config.sidenav.imageUrl));
	showCollapsePin$ = this.configService.config$.pipe(map((config) => config.sidenav.showCollapsePin));
	userVisible$ = this.configService.config$.pipe(map((config) => config.sidenav.user.visible));
	searchVisible$ = this.configService.config$.pipe(map((config) => config.sidenav.search.visible));

	version = Config.VERSION;

	userMenuOpen$: Observable<boolean> = of(false);

	items = this.navigationService.items.filter((item) => !item.alignBottom);
	footerItems = this.navigationService.items.filter((item) => item.alignBottom);

	private recentPanelRef: PopoverRef<RecentPanelData> | null = null;
	private recentCloseTimer: ReturnType<typeof setTimeout> | null = null;

	readonly sideNav = viewChild<ElementRef>('sideNav');
	impersonationEvent$: Subscription;

	constructor() {
		this.impersonationEvent$ = this.impersonationService.getImpersonationEvent().subscribe(() => {
			// this.setupMenuItems();
		});
	}

	ngOnInit() {
		this.setupMenuItems();
		this.pinService.syncUser(this.keycloak.getKeycloakInstance()?.tokenParsed?.sub);
		this.pinService.loadRecent();
	}

	setupMenuItems() {
		this.navigationService.items$.subscribe((newItems) => {
			this.items = newItems.filter((item) => !item.alignBottom);
			this.footerItems = newItems.filter((item) => item.alignBottom);
		});
	}

	ngAfterViewInit() {
		this.sessionService.sideNavWidth = this.sideNav()?.nativeElement?.offsetWidth;
	}

	collapseOpenSidenav() {
		this.layoutService.collapseOpenSidenav();
	}

	collapseCloseSidenav() {
		this.layoutService.collapseCloseSidenav();
	}

	toggleCollapse() {
		this.collapsed() ? this.layoutService.expandSidenav() : this.layoutService.collapseSidenav();
	}

	openProfileMenu(origin: HTMLDivElement): void {
		this.userMenuOpen$ = of(
			this.popoverService.open({
				content: UserMenuComponent,
				origin,
				offsetY: -8,
				width: origin.clientWidth,
				position: [
					{
						originX: 'center',
						originY: 'top',
						overlayX: 'center',
						overlayY: 'bottom',
					},
				],
			})
		).pipe(
			switchMap((popoverRef) => popoverRef.afterClosed$.pipe(map(() => false))),
			startWith(true)
		);
	}

	openSearch(): void {
		this.dialog.open(SearchModalComponent, {
			panelClass: 'vex-dialog-glossy',
			width: '100%',
			maxWidth: '600px',
		});
	}

	openQuickSearch(): void {
		this.dialog.open(AraRealestateSearchDialogComponent, {
			width: '600px',
			maxWidth: '95vw',
			height: '70vh',
			panelClass: 'ara-search-dialog-panel',
		});
	}

	chipTooltip(re: RecentRealEstate): string {
		return `${re.area}/${re.number}`;
	}

	pinRecent(re: RecentRealEstate): void {
		sessionStorage.setItem('selectedRealEstateArea', String(re.area));
		sessionStorage.setItem('selectedRealEstateID', re.id);
		this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
			this.router.navigate(['/realestates', re.id]);
		});
	}

	onRecentBtnEnter(origin: HTMLElement): void {
		this.cancelRecentClose();
		if (this.recentPanelRef) return;
		const recentRealEstates = [...this.pinService.recentRealEstates()].reverse();
		this.recentPanelRef = this.popoverService.open<RecentPanelData>({
			content: RecentPanelComponent,
			origin,
			hasBackdrop: false,
			data: {
				recentRealEstates,
				activeId: null,
				onSelect: (re: RecentRealEstate) => {
					this.closeRecentPanel();
					this.pinRecent(re);
				},
				onClear: () => {
					this.pinService.clearAll();
					this.closeRecentPanel();
				},
				onPanelEnter: () => this.cancelRecentClose(),
				onPanelLeave: () => this.scheduleRecentClose(),
			},
			width: 420,
			position: [
				{
					originX: 'end',
					originY: 'center',
					overlayX: 'start',
					overlayY: 'top',
				},
			],
			offsetX: 8,
		});
		this.recentPanelRef.afterClosed$.subscribe(() => {
			this.recentPanelRef = null;
		});
	}

	onRecentBtnLeave(): void {
		this.scheduleRecentClose();
	}

	private scheduleRecentClose(): void {
		this.cancelRecentClose();
		this.recentCloseTimer = setTimeout(() => this.closeRecentPanel(), 300);
	}

	private cancelRecentClose(): void {
		if (this.recentCloseTimer) {
			clearTimeout(this.recentCloseTimer);
			this.recentCloseTimer = null;
		}
	}

	private closeRecentPanel(): void {
		this.cancelRecentClose();
		this.recentPanelRef?.close();
		this.recentPanelRef = null;
	}

	@HostListener('window:resize', ['$event'])
	onResize(event) {
		this.sessionService.sideNavWidth = this.sideNav()?.nativeElement?.offsetWidth;
	}

	protected readonly trackByRoute = trackByRoute;
}
